import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export function FavoriteButton({ listingId, className = "", showLabel = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favorited, setFavorited] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { setFavorited(false); return; }
    let cancelled = false;
    api.get(`/favorites/check/${listingId}`)
      .then((r) => { if (!cancelled) setFavorited(!!r.data?.favorited); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [listingId, user]);

  const toggle = async (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!user) {
      toast.info("Connectez-vous pour ajouter aux favoris");
      navigate("/connexion");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/favorites/${listingId}`);
      setFavorited(!!data.favorited);
      toast.success(data.favorited ? "Ajouté aux favoris" : "Retiré des favoris");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={toggle}
      data-testid={`favorite-toggle-${listingId}`}
      disabled={busy}
      className={`inline-flex items-center justify-center gap-1.5 rounded-md transition-colors min-h-11 min-w-11 sm:min-h-9 sm:min-w-9 ${
        favorited
          ? "text-matpro-green"
          : "text-matpro-anthracite-muted hover:text-matpro-green"
      } ${className}`}
      aria-label={favorited ? "Retirer des favoris" : "Ajouter aux favoris"}
    >
      <Heart
        className={`h-5 w-5 ${favorited ? "fill-matpro-green stroke-matpro-green" : ""}`}
        strokeWidth={2}
      />
      {showLabel && <span className="text-xs font-semibold">Favoris</span>}
    </button>
  );
}
